import {
    Component,
    OnInit,
    ChangeDetectionStrategy,
    ViewChild,
    ElementRef,
    ChangeDetectorRef,
    AfterViewInit,
    OnDestroy
} from '@angular/core';
import {BsModalRef, BsModalService} from 'ngx-bootstrap';
import {BsModalEventsEmitter} from 'src/app/modules/shared/events/bsModal.events';
import {Subscription} from 'rxjs';
import {DiaryService} from 'src/app/modules/shared/services/diary.service';
import {filter} from 'rxjs/operators';
import {LocalisationService} from 'src/app/modules/shared/services/localisation.service';
import * as moment from 'moment';
import {MarkShoppingItemAsDeletedDto} from '../../../../shared/dto/markShoppingItemAsDeleted.dto';
import {FriendsComponent} from './friends.component';
import {AppLoadingEventsEmitter} from '../../../../shared/events/loading.events';
import {SendToFriendComponent} from './send-to-friend.component';
import {UpgradeComponent} from '../../../../shared/components/modals/upgrade/upgrade.component';
import {PricingService} from '../../../../pricing/services/pricing.service';
import {Constants} from '../../../../shared/config/constants.config';
import {VmvComponent} from './vmv.component';
import {UsersService} from '../../../../shared/services/users.service';

@Component({
    selector: 'app-shopping-list',
    templateUrl: './shopping-list.component.html',
    styleUrls: ['./shopping-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShoppingListComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly mobileWidth: number = 576;

    @ViewChild('wrapper') wrapper: ElementRef<HTMLElement>;

    subscriptions: Subscription[] = [];
    shoppingItems = [];

    fromDate: Date = this.getMonday(new Date());
    toDate: Date = new Date((new Date()).setDate((new Date()).getDate() - (new Date()).getDay() + (((new Date()).getDay() === 0 ? -6 : 1)) + 6));

    settings = {
        bigBanner: false,
        timePicker: false,
        format: 'dd.MM.yyyy',
        defaultOpen: false,
        closeOnSelect: true
    };

    friendsModal: BsModalRef;
    vmvModal: BsModalRef;
    friends = [];

    categories = [];
    showVmvButton = false;

    constructor(
        public readonly bsModalRef: BsModalRef,
        private readonly bsModalService: BsModalService,
        private readonly bsModalEventsEmitter: BsModalEventsEmitter,
        private readonly diaryService: DiaryService,
        private readonly localisationService: LocalisationService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly appLoadingEventsEmitter: AppLoadingEventsEmitter,
        private readonly pricingService: PricingService,
        private readonly usersService: UsersService,
        readonly constants: Constants
    ) {
    }

    ngOnInit() {
        this.subscriptions.push(
            this.bsModalEventsEmitter.bsModalStateChanged$.pipe(filter(bsModalRef => bsModalRef === this.bsModalRef)).subscribe(() => {
                this.init();
            })
        );


        const user = this.usersService.userProfile;
        if (user.id === 34 || user.id === 42) {
            // this.showVmvButton = true;
            this.changeDetectorRef.detectChanges();
        } else {
            this.showVmvButton = false;
            this.changeDetectorRef.detectChanges();
        }
    }

    ngAfterViewInit() {
        if (window.innerWidth <= this.mobileWidth) {
            this.toFullscreen();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    openFriendsModal() {
        this.friendsModal = this.bsModalService.show(FriendsComponent, {
            backdrop: true
        });

        this.friendsModal.content.shoppingListComponent = this;

        this.bsModalEventsEmitter.broadcastBsModalStateChanged(this.friendsModal);
    }

    setFriends(friends) {
        this.friends = friends;
        this.init();
    }

    onStartDateChange(event) {
        const fromDate = moment(this.fromDate).format('YYYY-MM-DD');
        const toDate = moment(this.toDate).format('YYYY-MM-DD');

        if (fromDate > toDate) {
            this.toDate = this.fromDate;
            this.changeDetectorRef.detectChanges();
        }

        this.init();
    }

    onToDateChange(event) {
        const fromDate = moment(this.fromDate).format('YYYY-MM-DD');
        const toDate = moment(this.toDate).format('YYYY-MM-DD');

        if (fromDate > toDate) {
            this.fromDate = this.toDate;
            this.changeDetectorRef.detectChanges();
        }

        this.init();
    }

    round(number: number): number {
        return Math.round(number);
    }

    changeShoppingItemStatus(event, index) {
        this.shoppingItems[index]['completed'] = !!event.target.checked;

        const data: MarkShoppingItemAsDeletedDto = {
            'item_id': this.shoppingItems[index]['id'],
            'delete': this.shoppingItems[index]['completed']
        };

        this.diaryService.markShoppingItemAsDeleted(data, moment(this.fromDate).format('YYYY-MM-DD'), moment(this.toDate).format('YYYY-MM-DD')).subscribe(response => {
            this.init();
        });
    }

    reload() {
        this.diaryService.clearShoppingItems(moment(this.fromDate).format('YYYY-MM-DD'), moment(this.toDate).format('YYYY-MM-DD')).subscribe(response => {
            this.init();
        });
    }

    private init() {
        this.appLoadingEventsEmitter.broadcastAppIsLoading(true);

        this.subscriptions.push(
            this.diaryService.getShoppingList(
                moment(this.fromDate).format('YYYY-MM-DD'),
                moment(this.toDate).format('YYYY-MM-DD'),
                this.friends.join(',')
            ).subscribe(list => {
                const shoppingList = [];
                const categories = [];
                const categoriesById = {};

                // tslint:disable-next-line:forin
                for (const i in list) {
                    const title = list[i]['title'];
                    let weight;

                    if (list[i]['type_calculation'] === 'WEIGHT') {
                        weight = Math.round(list[i]['weight']) + ' ' + this.localisationService.localise('abbreviation_gram');
                    } else {
                        weight = list[i]['qty'] + ' ' + this.localisationService.localise('br');
                    }

                    let category = null;

                    if (list[i]['category']) {
                        category = {id: list[i]['category']['id']};
                    }

                    shoppingList[i] = {
                        id: list[i]['id'],
                        task: title,
                        images: list[i]['images'],
                        weight: weight,
                        price: (list[i]['calculated_price'] > 0 ? list[i]['calculated_price'] + ' ' + this.localisationService.localise('currency') : null),
                        only_price: (list[i]['calculated_price'] > 0 ? list[i]['calculated_price'] : 0),
                        category: category,
                        completed: list[i]['mark_as_deleted']
                    };

                    if (list[i]['category'] && typeof categoriesById[list[i]['category']['id']] === 'undefined') {
                        categoriesById[list[i]['category']['id']] = {
                            id: list[i]['category']['id'],
                            title: list[i]['category']['title'],
                            sort_in_shop: list[i]['category']['sort_in_shop']
                        };
                        categories.push(categoriesById[list[i]['category']['id']]);
                    }
                }

                this.categories = categories.sort((a, b) => a.sort_in_shop - b.sort_in_shop);
                this.shoppingItems = shoppingList;

                this.appLoadingEventsEmitter.broadcastAppIsLoading(false);
                this.changeDetectorRef.markForCheck();
            })
        );
    }

    private toFullscreen() {
        const modalDialog = this.wrapper.nativeElement.parentElement.parentElement.parentElement;
        const modalContent = this.wrapper.nativeElement.parentElement.parentElement;
        const modal = this.wrapper.nativeElement.parentElement;

        modalDialog.style.borderRadius = '0';
        modalDialog.style.display = 'flex';
        modalDialog.style.margin = '0';
        modalDialog.style.padding = '0';
        modalDialog.style.width = '100%';
        modalDialog.style.maxWidth = 'unset';
        modalDialog.style.minHeight = '100%';
        modalDialog.style.height = '100%';
        modalContent.style.borderRadius = '0';
        modal.style.display = 'block';
        modal.style.height = '100%';
    }

    private getMonday(date) {
        return new Date(date.setDate((date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1))));
    }

    openSendToFriendModal() {
        this.pricingService.isFreePlan().subscribe(isFreePlan => {
            if (isFreePlan === true) {
                this.openUpgradeModal();
            } else {
                this.friendsModal = this.bsModalService.show(SendToFriendComponent, {
                    backdrop: true
                });

                this.friendsModal.content.shoppingListComponent = this;

                this.bsModalEventsEmitter.broadcastBsModalStateChanged(this.friendsModal);
            }
        });
    }

    openVmvModal() {
        this.vmvModal = this.bsModalService.show(VmvComponent, {
            backdrop: true
        });

        this.vmvModal.content.shoppingListComponent = this;

        this.bsModalEventsEmitter.broadcastBsModalStateChanged(this.vmvModal);
    }

    openUpgradeModal() {
        this.friendsModal = this.bsModalService.show(UpgradeComponent, {
            backdrop: true
        });

        this.bsModalEventsEmitter.broadcastBsModalStateChanged(this.friendsModal);
    }

    isIOS() {
        return [
                'iPad Simulator',
                'iPhone Simulator',
                'iPod Simulator',
                'iPad',
                'iPhone',
                'iPod'
            ].includes(navigator.platform)
            // iPad on iOS 13 detection
            || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    }

    calculateTotal() {
        let total = 0;

        // tslint:disable-next-line:forin
        for (const i in this.shoppingItems) {
            total += parseFloat(this.shoppingItems[i]['only_price']);
        }

        return total.toFixed(2) + ' ' + this.localisationService.localise('currency');
    }

}
